import { jwtDecode } from "jwt-decode";
import dayjs from "dayjs";
import { notification } from "antd";
import { FilterGroup, FilterItem, FilterOperatorEnum, LogicalOperatorEnum } from "interfaces/baseTypes";
import { EntityMetaData, EnumInfo, PatchRequest, PatchRequestItem } from "interfaces/apiTypes.js";
import { ILanguage } from "interfaces/index";
import type { ProductGroupType } from "apiClients/useProductGroupOpenApiClient";

export enum RelationType {
  Manual = "Manual",
  SafetySheet = "SafetySheet",
  DimensionalSheet = "DimensionalSheet",
  ProductImage = "ProductImage",
  SpareParts = "SpareParts",
  RelatedParts = "RelatedParts",
  Accessories = "Accessories",
}

// ProductGroup Type utilities
export const getProductGroupTypeText = (type?: ProductGroupType): string => {
  switch (type) {
    case 0:
      return 'Serie';
    case 1:
      return 'Andra färger';
    case 2:
      return 'Andra storlekar';
    default:
      return 'Okänd';
  }
};

export const getProductGroupTypeColor = (type?: ProductGroupType): string => {
  switch (type) {
    case 0:
      return 'blue';
    case 1:
      return 'green';
    case 2:
      return 'orange';
    default:
      return 'default';
  }
};

export const getProductGroupTypeOptions = () => [
  { value: 0 as ProductGroupType, label: 'Serie' },
  { value: 1 as ProductGroupType, label: 'Andra färger' },
  { value: 2 as ProductGroupType, label: 'Andra storlekar' },
];

export const stringToColor = (str: string) => {
  if (str == "Focus") {
    return "#005488";
  }

  if (str == "Tura") {
    return "#000";
  }

  if (str == "pågående") {
    return "#23b06e";
  }

  if (str == "avslutat") {
    return "#3eb2fa";
  }

  if (str == "pausat") {
    return "#f5ad42";
  }

  let hash = 0;
  str.split("").forEach((char) => {
    hash = char.charCodeAt(0) + ((hash << 5) - hash);
  });
  let colour = "#";
  for (let i = 0; i < 3; i++) {
    const value = (hash >> (i * 8)) & 0xff;
    colour += value.toString(16).padStart(2, "0");
  }
  return colour;
};

export const hashCode = (value: string) => {
  var hash = 0,
    i,
    chr;
  if (value?.length === 0) return hash;
  for (i = 0; i < value?.length; i++) {
    chr = value?.charCodeAt(i);
    hash = (hash << 5) - hash + chr;
    hash |= 0; // Convert to 32bit integer
  }
  return hash;
};

export function getUniqueValuesFromArrayInArray<T>(
  dataSource: readonly T[],
  key: keyof T
): string[] {
  const allIds = dataSource?.flatMap((obj) => obj[key] as unknown as string);
  const uniqueIDs = new Set<string>(allIds);
  return Array.from<string>(uniqueIDs);
}

export const decodeTokenAndSave = (response: any) => {
  const token = response?.data?.token;
  const decodedTokenObj = jwtDecode(token);
  localStorage.setItem("user", JSON.stringify({ ...decodedTokenObj, token }));
};

export const getUserInfoFromCache = () => {
  try {
    const data = localStorage.getItem("user");
    const userInfo = data ? JSON.parse(data) : null;
    return userInfo;
  } catch (e) {
    console.error(e);
  }
  return null;
};

/** Roles from the JWT `role` claim (comma-separated). */
export const getUserRoles = (): string[] => {
  const role = getUserInfoFromCache()?.role;
  if (!role || typeof role !== "string") {
    return [];
  }
  return role.split(",").map((r: string) => r.trim()).filter(Boolean);
};

export const isAdmin = (): boolean => getUserRoles().includes("Admin");

export const isAdminOrSuperAdmin = (): boolean => {
  const roles = getUserRoles();
  return roles.includes("SuperAdmin") || roles.includes("Admin");
};

/**
 * Supplier portal mode: has Supplier, and is not Admin/SuperAdmin.
 * Those elevated roles keep the full PIM UI even if Supplier is also assigned.
 */
export const isSupplierOnly = (): boolean => {
  if (isAdminOrSuperAdmin()) {
    return false;
  }
  return getUserRoles().includes("Supplier");
};

export const getDescriptionFromLocale = (locale: string) => {
  if (locale == "sv") {
    return "Svenska";
  }

  if (locale == "da") {
    return "Danska";
  }

  if (locale == "no") {
    return "Norska";
  }

  return "Svenska";
};

export const formatDate = (rawDate: string) => {
  const date = dayjs(rawDate?.toString());
  return date.format("YYYY-MM-DDTHH:mm:ss");
};

export function splitArrayEvenlyIntoThreeChunks<T>(
  arr: T[] | undefined
): [T[], T[], T[]] {
  // Return empty arrays if input is not an array
  if (!Array.isArray(arr)) {
    return [[], [], []];
  }

  let part1: T[] = [],
    part2: T[] = [],
    part3: T[] = [];
  let totalItems: number = arr.length;
  let partSizeBase: number = Math.floor(totalItems / 3);
  let extraItems: number = totalItems % 3;

  // Determine the sizes of each part dynamically
  let part1Size: number = partSizeBase + (extraItems > 0 ? 1 : 0);
  let part2Size: number = partSizeBase + (extraItems > 1 ? 1 : 0);

  for (let i = 0; i < arr.length; i++) {
    if (i < part1Size) {
      part1.push(arr[i]);
    } else if (i < part1Size + part2Size) {
      part2.push(arr[i]);
    } else {
      part3.push(arr[i]);
    }
  }

  return [part1, part2, part3];
}

export function copyToClipboard(text: string) {
  navigator.clipboard.writeText(text).then(
    function () {
      notification.info({
        message: "Text kopierad",
        duration: 1,
      });
    },
    function (err) {
      console.error("Could not copy text: ", err);
    }
  );
}

export function areArraysEqual<T>(
  a: T[] | null | undefined,
  b: T[] | null | undefined,
  comparator: (x: T, y: T) => boolean = (x, y) => x === y
): boolean {
  // Check if both are null or undefined, consider them equal
  if (a === b) return true;

  // If either is null/undefined and not both, they are not equal
  if (a == null || b == null) return false;

  // Proceed with length and content checks
  if (a.length !== b.length) return false;
  for (let i = 0; i < a.length; i++) {
    if (!comparator(a[i], b[i])) return false;
  }
  return true;
}

export const formatISO8601DateString = (
  dateStr: string | null | undefined,
  delimiter: "/" | "-" = "/",
  withTimestamp: boolean = false
): string | null => {
  if (!dateStr) {
    // console.error('Date string is null, undefined or empty.');
    return null;
  }

  try {
    const date = new Date(dateStr);

    if (isNaN(date.getTime())) {
      // console.error('Invalid date string.');
      return null;
    }

    const year = date.getFullYear();
    const month = (date.getMonth() + 1).toString().padStart(2, "0");
    const day = date.getDate().toString().padStart(2, "0");

    // Create the date string with the desired delimiter
    let formattedDate = `${day}${delimiter}${month}${delimiter}${year}`;

    if (withTimestamp) {
      const hours = date.getHours().toString().padStart(2, "0");
      const minutes = date.getMinutes().toString().padStart(2, "0");
      const seconds = date.getSeconds().toString().padStart(2, "0");
      formattedDate = `${formattedDate} ${hours}:${minutes}:${seconds}`;
    }

    return formattedDate;
  } catch (error) {
    // console.error('An error occurred while formatting the date:', error);
    return null;
  }
};

export function calculatePercentageDiff(
  oldPrice: number,
  newPrice: number,
  roundOffDecimals: number = 2
): number | null {
  if (oldPrice === 0) {
    return null;
  }
  const difference = ((newPrice - oldPrice) / oldPrice) * 100;
  return parseFloat(difference.toFixed(roundOffDecimals));
}

export const sleep = (ms: number): Promise<void> => {
  return new Promise(resolve => setTimeout(resolve, ms));
};

// Function to format date with time (handles Date, string, and undefined inputs)
export function formatDateWithTime(dateInput: Date | string | undefined): string {
  if (!dateInput) {
    return "No Date"; // Return fallback value for undefined or null inputs
  }

  // Ensure input is converted to a Date object
  const parsedDate = new Date(dateInput);

  // Check if the date is valid
  if (isNaN(parsedDate.getTime())) {
    return "Invalid Date";
  }

  return parsedDate.toLocaleString("sv-SE", {
    year: "numeric",
    month: "long",
    day: "numeric",
    hour: "numeric",
    minute: "numeric",
    second: "numeric",
    timeZone: "Europe/Stockholm",
  });
}

// export const addOrUpdateFilterGroupItem = (
//   filterGroup: FilterGroup,
//   fieldName: string,
//   value: string,
//   operator: FilterOperatorEnum
// ): FilterGroup => {
//   const newFilterItem: FilterItem = {
//     fieldName: fieldName,
//     value: value,
//     operator: operator
//   };

//   // Create a deep copy of the filter group to avoid mutating the original
//   const updatedFilterGroup = { ...filterGroup };
  
//   // Recursively search through the filter group
//   const searchAndUpdate = (group: FilterGroup): boolean => {
//     for (let i = 0; i < group.value.length; i++) {
//       const item = group.value[i];
      
//       if (Array.isArray(item.value)) {
//         // If it's a nested group, recurse
//         if (searchAndUpdate(item as FilterGroup)) {
//           return true;
//         }
//       } else if (item.fieldName === fieldName) {
//         // Found matching field, update it
//         group.value[i] = newFilterItem;
//         return true;
//       }
//     }
//     return false;
//   };

//   // If item wasn't found and updated, add it to the root level
//   if (!searchAndUpdate(updatedFilterGroup)) {
//     updatedFilterGroup.value.push(newFilterItem);
//   }

//   return updatedFilterGroup;
// };

export const addOrUpdateFilterItem = (
  filters: FilterItem[],
  setFilters: React.Dispatch<React.SetStateAction<FilterItem[]>>,
  fieldName: string,
  value: string,
  operator: FilterOperatorEnum
): FilterItem => {
  const newFilterItem: FilterItem = {
    fieldName: fieldName,
    value: value,
    operator: operator
  };

  // Check if the filter already exists
  const index = filters.findFilterIndexByFieldName(fieldName);
  if (index !== -1) {
    // Update existing filter value
    filters[index].value = value;
  } else {
    // Add new filter item
    filters.push(newFilterItem);
  }

  // Update the state with the new filters array
  setFilters([...filters]);
  return newFilterItem;
};

export const removeFilterItem = (filters: FilterItem[], setFilters: React.Dispatch<React.SetStateAction<FilterItem[]>>, fieldName: string): FilterItem[] => {
  const newFilters = filters.filter(filter => filter.fieldName !== fieldName);
  setFilters(newFilters);
  return newFilters;
};

export const convertFiltersToString = (filters: FilterItem[]): string => {
  return filters.map(filter => 
      `${filter.fieldName}::${filter.operator}::${filter.value}`
  ).join('@@');
};

export const parseFiltersToString = (filter: FilterGroup | FilterItem, includeParentheses: boolean = false): string => {
  if(filter == null || (Array.isArray(filter.value) && filter.value.length === 0)) {
    return "";
  }
  // If array and fieldName does not exist, it is a FilterGroup
  if (Array.isArray(filter.value) && !('fieldName' in filter.value)) {
    const groupString = filter.value
      .map((item) => parseFiltersToString(item))
      .join(`${filter.operator.toUpperCase()}`);
    // Add parentheses based on operator precedence
    if (filter.operator === LogicalOperatorEnum.AND) {
      return includeParentheses ? `(${groupString})` : groupString;
    } else if (filter.operator === LogicalOperatorEnum.OR) {
      return includeParentheses ? `(${groupString})` : groupString;
    }

    return groupString;
  } else {
    const filterItem = filter as FilterItem;
    if(!filterItem.value || filterItem.value === ""){
      return `${filterItem.fieldName}::${filterItem.operator}`;
    }
    return `${filterItem.fieldName}::${filterItem.operator}::${filterItem.value}`;
  }
};

export const parseStringToFilters = (filterString: string, fallbackOperator: LogicalOperatorEnum = LogicalOperatorEnum.AND): FilterGroup => {
  if (!filterString || filterString.length === 0) {
    return {
      operator: fallbackOperator,
      value: []
    };
  }

  // Case for OR operator (||)
  if (filterString.includes('||')) {
    const parts = filterString.split('||');
    return {
      operator: LogicalOperatorEnum.OR,
      value: parts.map(part => {
        const [fieldName, operator, value] = part.split('::');
        return { fieldName, operator, value } as FilterItem;
      })
    };
  }
  
  // Case for AND operator (@@)
  if (filterString.includes('@@')) {
    const parts = filterString.split('@@');
    return {
      operator: LogicalOperatorEnum.AND,
      value: parts.map(part => {
        const [fieldName, operator, value] = part.split('::');
        return { fieldName, operator, value } as FilterItem;
      })
    };
  }

  // Case for single filter
  const [fieldName, operator, value] = filterString.split('::');
  return {
    operator: fallbackOperator,
    value: [{ fieldName, operator, value } as FilterItem]
  };
}

export const convertFiltersToStringIncludeLanguage = (filters: FilterItem[], activeLanguage: ILanguage | null, fieldName: string = "languageId"): string => {
  return filters?.concat(ensureLanguageFilterItem(filters, fieldName, activeLanguage)).map(filter => 
      `${filter.fieldName}::${filter.operator}::${filter.value}`
  ).join('@@');
};

export const getEnumValuesFromMetadata = (metadata: EntityMetaData, className: string, fieldName: string): EnumInfo[] => {
  const classMetadata = metadata.metaData?.get?.find(c => c.fullName == className);
  if (!classMetadata) {
    return [];
  }
  const enumInfo = classMetadata.fields?.find(f => f.name == fieldName)?.enumInfo ?? [];
  return enumInfo.map(enumItem => ({
    ...enumItem,
    translationKey: `${className}.${fieldName}.${enumItem.enumValue}`
  }));
};

export const ensureLanguageFilterItem = (filterItems: FilterItem[], fieldName: string , activeLanguage: ILanguage | null ): FilterItem[] => {
  return filterItems.find(filter => filter.fieldName == fieldName) ? filterItems : [...filterItems, {fieldName: fieldName, operator: FilterOperatorEnum.EQUALS, value: activeLanguage?.id ?? 1}];
};

export const translateEnumValues = (enumValues: EnumInfo[], t: (key: string) => string): EnumInfo[] => {
  return enumValues.map(enumItem => ({
    ...enumItem,
    enumDisplayValue: t(enumItem.translationKey ?? "")
  }));
};

export const generatePatchRequest = <T extends object>(object: T): PatchRequest => {
  return {
    values: Object.keys(object).map(key => ({
      propertyPath: key,
      value: object[key as keyof T] ?? null
    }))
  };
};

export const convertFilterStringToQueryParams = (paramName: string, filterString: string): string => {
  if (!filterString) {
    return '';
  }
  return `${paramName}=${encodeURIComponent(filterString)}`;
};

export function encodeIds(ids: number[]): string {
  const jsonString = JSON.stringify(ids);
  return btoa(jsonString); // Base64 encode
}

export function decodeIds(encoded: string): number[] {
  const jsonString = atob(encoded); // Base64 decode
  return JSON.parse(jsonString);
}


